import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { logAction } from "@/api/activity";
import { getCompany } from "@/api/companies";
import { createContact, updateContact } from "@/api/contacts";
import {
  completeCorrection,
  type CorrectionContact,
  type CorrectionQueueItem,
} from "@/api/enrichment";
import type { CompanyDetail } from "@/api/types";
import { EnrichmentCard } from "./EnrichmentCard";
import { EnrichmentSessionSetup } from "./EnrichmentSessionSetup";
import { ReservationWindow } from "./ReservationWindow";
import { SessionBatchList } from "./SessionBatchList";
import { SessionExpiryBanner } from "./SessionExpiryBanner";
import { useEnrichmentSession } from "@/hooks/useEnrichmentSession";

interface EnrichmentCorrectionsProps {
  slug: string;
  /** False while the tab is mounted but hidden — see EnrichmentWork. */
  active: boolean;
  /** Rows the corrections queue can hand out right now, or null while
   *  unknown. Zero is legitimate on a project that never flagged a
   *  "Wrong Owner" — the refusal for that lands on the session start. */
  queueCount: number | null;
  onQueueChanged: () => void;
}

/** Corrections queue — companies flagged "Wrong Owner" whose contact
 *  data a sales rep reported as wrong. Unpaid rework, so the backend
 *  logs it with `is_correction=true` and zero earnings.
 *
 *  Save is a two-call flow: PATCH the contact, then POST the complete
 *  endpoint, which drops the tag, applies the phone-only status rule
 *  and writes the activity row in one transaction. */
export function EnrichmentCorrections({
  slug,
  active,
  queueCount,
  onQueueChanged,
}: EnrichmentCorrectionsProps) {
  const { session, isOpening, openError, isEnding, open, end, skip } =
    useEnrichmentSession(slug, "corrections", onQueueChanged);
  // Local copy of the reserved batch. Kept mutable because completing a
  // correction drops the row here (see dropCurrent) — the batch itself is
  // fixed server-side, so this only affects what is left to walk.
  const [queue, setQueue] = useState<CorrectionQueueItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [correctedCount, setCorrectedCount] = useState(0);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [isSaving, setIsSaving] = useState(false);

  const [fullName, setFullName] = useState("");
  const [title, setTitle] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  // The corrections payload carries no tax_id or notes, so the company
  // is fetched per item to drive the Pappers link and the read-only
  // notes panel. Same per-cursor fetch the Work tab does for contacts.
  const [company, setCompany] = useState<CompanyDetail | null>(null);

  const current = queue[currentIndex];

  // Built from the LIVE queue: a closed correction leaves it, so the rail
  // shows what is still to do rather than a list with holes in it.
  const batchListItems = useMemo(
    () =>
      queue.map((item) => ({
        key: item.company_id,
        primary: item.name,
        secondary: item.contact?.full_name ?? item.city ?? null,
      })),
    [queue],
  );

  // The batch replaces the old "pull every page up front" load. That
  // workaround existed because completing a correction removed its tag and
  // shifted every later row up a slot, so lazy paging skipped companies.
  // A reserved batch is fixed for the length of the session, so the drift
  // it guarded against cannot happen.
  useEffect(() => {
    setQueue(session?.corrections ?? []);
    setCurrentIndex(0);
  }, [session]);

  const startSession = useCallback(
    async (count: Parameters<typeof open>[0]) => {
      await open(count);
    },
    [open],
  );

  // Pre-fill from the current (wrong) values shipped with the item —
  // the contractor verifies and fixes rather than typing from scratch.
  useEffect(() => {
    if (!current) return;
    const c: CorrectionContact | null = current.contact;
    setFullName(c?.full_name ?? "");
    setTitle(c?.title ?? "");
    setLinkedin(c?.linkedin_url ?? "");
    setPhone(c?.phone ?? "");
    setEmail(c?.email ?? "");
    setStartTime(Date.now());
    window.setTimeout(() => nameRef.current?.focus(), 50);
  }, [current]);

  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    setCompany(null);
    getCompany(slug, current.company_id)
      .then((c) => {
        if (!cancelled) setCompany(c);
      })
      .catch(() => {
        // Non-blocking: without it we just lose the Pappers link and
        // the notes panel, the correction itself still works.
        if (!cancelled) setCompany(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, current]);

  /** Skip: leave the company in place and step over it. */
  function advance() {
    setCurrentIndex((i) => i + 1);
  }

  /** Completion: the company just left the server-side queue, so drop
   *  it here too. The index deliberately stays put — the next company
   *  slides into the freed slot, which keeps the local list aligned
   *  with what the server would return. */
  function dropCurrent() {
    setQueue((q) => q.filter((_, i) => i !== currentIndex));
  }

  function elapsedSeconds() {
    return Math.max(1, Math.round((Date.now() - startTime) / 1000));
  }

  /** Same vocabulary the classic flow logs, compared against the wrong
   *  values that came down with the item. */
  function changedFields(): string[] {
    const c = current?.contact;
    const out: string[] = [];
    if (!c) {
      if (fullName.trim()) out.push("contact_created");
      return out;
    }
    if (fullName.trim() !== (c.full_name ?? "")) out.push("name");
    if (title !== (c.title ?? "")) out.push("title");
    if (linkedin !== (c.linkedin_url ?? "")) out.push("linkedin");
    if (phone !== (c.phone ?? "")) out.push("phone");
    if (email !== (c.email ?? "")) out.push("email");
    return out;
  }

  async function handleSubmit(e?: FormEvent) {
    if (e) e.preventDefault();
    if (isSaving || !current) return;
    const trimmedPhone = phone.trim();
    // Phone is the whole point of a correction — a fix that leaves the
    // number empty fixes nothing. "Can't find" is the escape hatch.
    if (!trimmedPhone) return;
    setIsSaving(true);
    try {
      const changes = changedFields();
      const timeSpentSeconds = elapsedSeconds();
      const name = fullName.trim();
      const payload = {
        title: title.trim() || null,
        linkedin_url: linkedin.trim() || null,
        phone: trimmedPhone,
        email: email.trim() || null,
      };

      // 1. Contact fields. The complete endpoint deliberately doesn't
      // write them, so this has to land first.
      if (current.contact) {
        await updateContact(slug, current.contact.id, {
          full_name: name || current.contact.full_name,
          ...payload,
        });
      } else if (name) {
        await createContact(slug, {
          company_id: current.company_id,
          full_name: name,
          ...payload,
          is_decision_maker: true,
        });
      }

      // 2. Close it out. Tag removal + status rule + activity log are
      // one backend transaction; it's idempotent, so a retry after a
      // failure here is safe.
      const res = await completeCorrection(slug, current.company_id, {
        changes,
        time_spent_seconds: timeSpentSeconds,
      });

      if (res.status_updated) {
        toast.success("Corrected, now To Be Contacted");
      } else if (res.status_skipped_reason === "no_phone") {
        // Normal outcome for an email-only fix, not a failure: the row
        // is saved but still has no number to call.
        toast.success("Saved, but still not callable: no phone number");
      } else {
        toast.success("Corrected");
      }
      setCorrectedCount((n) => n + 1);
      dropCurrent();
    } catch (err) {
      // Do NOT advance: the item stays so the contractor can retry.
      toast.error("Couldn't save the correction, try again");
      console.warn("[Corrections] submit failed", err);
    } finally {
      setIsSaving(false);
    }
  }

  /** Releases the row back to the pool and steps over it. The only
   *  release this side owns: completing a correction frees the row inside
   *  the backend transaction, so no skip call there.
   *
   *  A skipped company can be handed to this same operator again in a
   *  LATER session — it only leaves the current batch. */
  function handleSkip() {
    if (!current) return;
    void skip(current.company_id);
    advance();
  }

  /** Can't find: the same activity the classic flow posts, plus the
   *  correction flag. No complete call, so the tag stays and the
   *  company remains queued. */
  async function handleCantFind() {
    if (!current) return;
    try {
      await logAction(slug, current.company_id, {
        action: "enrichment_completed",
        details: {
          changes: [],
          result: "not_found",
          is_correction: true,
          time_spent_seconds: elapsedSeconds(),
        },
      });
    } catch (err) {
      console.warn("[Corrections] cant-find log failed", err);
    }
    advance();
  }

  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable
      ) {
        return;
      }
      if (e.key === "s" || e.key === "S") handleSkip();
      else if (e.key === "x" || e.key === "X") void handleCantFind();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, currentIndex, current?.company_id]);

  if (!session) {
    return (
      <EnrichmentSessionSetup
        title="New corrections session"
        description="Reserve a batch of companies sales flagged as wrong data. Unpaid rework, so it never counts towards earnings."
        isOpening={isOpening}
        error={openError}
        onStart={(n) => void startSession(n)}
        queueCount={queueCount}
        accent="amber"
      />
    );
  }

  // Size of the batch as it was handed over. `queue` shrinks as
  // corrections close, so it cannot tell "reserved nothing" from
  // "corrected everything" — the reservation can.
  const reservedCount = session.corrections?.length ?? 0;

  if (reservedCount === 0) {
    return (
      <div className="mx-auto max-w-lg py-20 text-center">
        <div className="mb-4 text-6xl">🗒️</div>
        <h2 className="text-xl font-bold text-slate-900">
          Nothing flagged right now
        </h2>
        <p className="mt-2 text-slate-500">
          {session.queue_remaining > 0
            ? `Nothing was free to reserve just now — the ${session.queue_remaining} flagged compan${session.queue_remaining === 1 ? "y is" : "ies are"} being worked by other operators.`
            : "No company is flagged as wrong data at the moment."}
        </p>
        <p className="mt-1 text-sm text-slate-400">
          Come back later, or try a smaller batch.
        </p>
        <Button
          onClick={() => void end()}
          className="mt-6"
          disabled={isEnding}
        >
          Back to setup
        </Button>
      </div>
    );
  }

  if (!current || currentIndex >= queue.length) {
    return (
      <div className="mx-auto max-w-lg py-20 text-center">
        <div className="mb-4 text-6xl">🎉</div>
        <h2 className="text-xl font-bold text-slate-900">Session complete</h2>
        <p className="mt-2 text-slate-500">
          {reservedCount} compan{reservedCount === 1 ? "y" : "ies"} processed,{" "}
          {correctedCount} corrected.
        </p>
        <p className="mt-1 text-sm text-slate-400">
          {session.queue_remaining} still flagged in the queue.
        </p>
        <Button
          onClick={() => void end()}
          className="mt-6"
          disabled={isEnding}
        >
          End session
        </Button>
      </div>
    );
  }

  const linkedinSearchQuery = encodeURIComponent(
    `"${fullName}" "${current.name}" site:linkedin.com/in`,
  );

  return (
    <div className="mx-auto max-w-[1400px] space-y-4">
      <SessionExpiryBanner expiresAt={session.expires_at} />
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-slate-500">
            {Math.min(currentIndex + 1, queue.length)} / {queue.length} in batch
          </span>
          {/* Still flagged and free for OTHER operators — server-side
            *  count, not the remainder of this batch. */}
          <span className="text-xs text-slate-400">
            {session.queue_remaining} left in queue
          </span>
          {/* Sized from the lot server-side — not a flat three hours. */}
          <ReservationWindow expiresAt={session.expires_at} />
        </div>
        <div className="flex items-center gap-3">
          <span className="font-medium text-amber-600">
            Corrected: {correctedCount}
          </span>
          <button
            type="button"
            onClick={() => void end()}
            disabled={isEnding}
            className="text-xs text-slate-400 hover:text-rose-600 disabled:opacity-50"
            title="Release the companies you have not worked yet"
          >
            End session
          </button>
        </div>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-amber-500 transition-all duration-300"
          style={{
            width: `${queue.length > 0 ? (currentIndex / queue.length) * 100 : 0}%`,
          }}
        />
      </div>

      {/* Card and batch list side by side once there is room — see
        *  EnrichmentWork for why the rail earns its column. */}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="mx-auto w-full min-w-0 max-w-3xl">
          <EnrichmentCard
            mode="correction"
            companyName={current.name}
            taxId={company?.tax_id}
            city={current.city}
            postalCode={company?.postal_code}
            companyNotes={company?.notes}
            recentComments={current.recent_comments}
            fullName={fullName}
            onFullName={setFullName}
            title={title}
            onTitle={setTitle}
            linkedin={linkedin}
            onLinkedin={setLinkedin}
            phone={phone}
            onPhone={setPhone}
            email={email}
            onEmail={setEmail}
            linkedinSearchQuery={linkedinSearchQuery}
            autoFocusRef={nameRef}
            isSaving={isSaving}
            canSubmit={!!phone.trim()}
            onSubmit={handleSubmit}
            onSkip={handleSkip}
            onCantFind={() => void handleCantFind()}
          />
        </div>

        <div className="hidden xl:block">
          {/* `processed` and `total` are passed because this queue's list
            *  shrinks: a closed correction is removed and the cursor stays
            *  put, so neither the index nor the list length can count the
            *  work on its own. */}
          <SessionBatchList
            items={batchListItems}
            currentIndex={currentIndex}
            processed={correctedCount + currentIndex}
            total={reservedCount}
            accent="amber"
          />
        </div>
      </div>
    </div>
  );
}
